<script lang="ts">
    import { onMount, getContext, tick } from 'svelte';
    import FutureStartNote from "./FutureStartNote.svelte";
    import { LocalDate } from "@/time";
    import { initForecastInputs } from "@/Util/api";
    import { durationToHoursMinutes, getCurrentFitnessSignature, getCurrentTrainingLoads, round, specificityToRating, startDateFromInput, type XSSDetails } from '@/Util/utils';
    import { getLibxert } from '@/xert-wasm';
    import { addDays } from 'date-fns';
    import {
        eventOptions,
        ProgramType,
        specificityThreshold,
        defaultEventPlan,
        emptyForecastedTargets,
        eventReadinessBounds,
        getFocus,
    } from "@/Common/constants";
    import type {
        TerrainTypes, 
        XfaiFormProps,
    } from "@/Common/constants";
    import { DatePicker, InfoIcon, NumberInput, SelectSlider } from "@/Common";
    import {ProgramAchievabilityMessage, ProgramAchievabilityStatusIcon, ProgramFitnessSignature, ProgramTrainingLoad, XfaiXss} from './ProgramElements';
    import { createProgramAchievability } from './modules/ProgramAchievability';
    import { createForecastInputs, type EventForecastTargets, estimateTargets } from './modules/ForecastFunctions';
    import { createUserFocusPowers } from './modules/UserFocusPowers';
    import type { MyFitnessStores } from '@/MyFitness/stores';
    import { writable } from 'svelte/store';
    import XfaiGeneralSettings from './ProgramElements/XfaiGeneralSettings.svelte';
    import WeeklyHoursSlider from './ProgramElements/SetupFormInputs/WeeklyHoursSlider.svelte';
    import EventReadiness from './ProgramElements/EventReadiness.svelte';
    import TrainPrefDayPicker from './ProgramElements/TrainPrefDayPicker.svelte';
    import { ToggleButtonSmall } from '@/Bootstrap';
    import { slide } from 'svelte/transition';
    import * as RadioGroup from "@/lib/components/ui/radio-group";
    import Label from "@/lib/components/ui/label/label.svelte";
    import type { EventTargetsMode } from "@/Common/constants";
    
    const myFitness = getContext<MyFitnessStores>('myFitness')
    const { userParams, trainingAdvice } = myFitness
    let currentSignature: XSSDetails
    let currentTL: XSSDetails

    function reactiveUpdate() {
        currentSignature = getCurrentFitnessSignature($trainingAdvice)
        currentTL = getCurrentTrainingLoads($trainingAdvice)
        focusPower.updateFocusPower($plan.targetAthlete)
        if(initialInputs) updateInitialInputs() // Ensures it doesn't run on first load
    }
    // $: if($trainingAdvice) reactiveUpdate()
    $: if($trainingAdvice && !disabled) reactiveUpdate()
    
    type $$Props = XfaiFormProps<ProgramType.Event>
    export let plan = writable({...defaultEventPlan})
    export let forecastInputs: any
    export let disabled: boolean
    // Updating the existing plan can't move its start date, so the field is hidden then.
    export let hideStartDate = false
    export let achievabilityId = "event-achievability"
    export const forecastReady = true
    export const refresh = updateInitialInputs
    let className = ""
    export {className as class}

    /* Date input targetDate */
    let targetDate: string
    let startDate: string = LocalDate.format($plan.startDate ?? new Date())

    /* Current Focus Power and Duration */
    let focusPower = createUserFocusPowers(trainingAdvice)
    const {currentFocusPower, focusPowerDuration} = focusPower

    /* Targets */
    let targetSignature: XSSDetails
    let targetTL: XSSDetails
    let targetXSSBreakdown: XSSDetails
    let eventReadinessLocked = true

    /* Manual Event XSS */
    let manualXlss: number | undefined = $plan.manualXss?.xlss
    let manualXhss: number | undefined = $plan.manualXss?.xhss
    let manualXpss: number | undefined = $plan.manualXss?.xpss
    const isManual = () => $plan.targetsMode === "manual"

    function onTargetsModeChange() {
        if (isManual() && !$plan.manualXss) {
            // Start from the XSS of the chosen event
            if (targetXSSBreakdown) {
                manualXlss = Math.round(targetXSSBreakdown.xlss)
                manualXhss = round(targetXSSBreakdown.xhss, 1)
                manualXpss = round(targetXSSBreakdown.xpss, 1)
            }
            setManualXss()
        }
        updateEventPlan()
        updateEventTargets()
    }

    // Focus and specificity of the manual XSS (the chosen event's are used otherwise, see updateEventPlan)
    async function setFocusFromManualXss() {
        const xss = $plan.manualXss
        if (!xss) return
        const libxert = await getLibxert()
        const sig = $trainingAdvice.signature
        const sfd = libxert.get_focus_duration(sig.pp, sig.ftp, sig.atc, xss.xlss, xss.xhss, xss.xpss)
        const sp = libxert.get_specificity(sig.pp, sig.ftp, sig.atc, xss.xlss, xss.xhss, xss.xpss)
        $plan.targetAthlete = getFocus($userParams.athletes, sfd)
        $plan.specificity = specificityToRating(sp)
        focusPower.updateFocusPower($plan.targetAthlete)
    }

    function onTargetsModeValueChange(value: string | undefined) {
        $plan.targetsMode = value as EventTargetsMode
        onTargetsModeChange()
    }

    function setManualXss() {
        const nonNegative = (v: number | undefined): v is number => v !== undefined && Number.isFinite(v) && v >= 0
        if (!nonNegative(manualXlss) || !nonNegative(manualXhss) || !nonNegative(manualXpss) || manualXlss <= 0) return false
        $plan.manualXss = { xlss: manualXlss, xhss: manualXhss, xpss: manualXpss }
        return true
    }

    function onManualXssInput() {
        if (setManualXss()) {
            updateEventPlan()
            updateEventTargets()
        }
    }

    /* Achievability Module*/
    export const programAchievability  = createProgramAchievability(ProgramType.Event)
    const {overallAchievability, hoursAchievability, statusMessage, targetAchievability} = programAchievability
    function validateTargets() {
        const difficulty = !$plan.settings.difficultyFromTrainingLoad ? $plan.settings.difficulty : null
        return programAchievability.validateTargets($plan.targetDate, new Date(initialInputs.t0*1000), targetTL, currentTL, $plan.settings.maxWeeklyHours, difficulty)
    }

    function getHourlyXss(selectedEvent: {highest_event_xssr: number, lowest_event_xssr: number, max_duration: number}, durationMinutes: number) {
        const maxHours = selectedEvent.max_duration
        const hours = Math.max(1, Math.min(maxHours, durationMinutes / 60))
        const t = maxHours <= 1 ? 0 : (hours - 1) / (maxHours - 1)
        return selectedEvent.highest_event_xssr + t * (selectedEvent.lowest_event_xssr - selectedEvent.highest_event_xssr)
    }

    $: maxDurationMinutes = (eventOptions[$plan.type]?.terrains[$plan.terrain]?.max_duration ?? 12) * 60

    function updateEventPlan() {
        let selectedEvent = eventOptions[$plan.type].terrains[$plan.terrain]
        $plan.specificity = selectedEvent.specificity
        $plan.targetAthlete = selectedEvent.focus
        const maxMin = selectedEvent.max_duration * 60
        if ($plan.duration > maxMin) $plan.duration = maxMin
        const hourlyXss = getHourlyXss(selectedEvent, $plan.duration)
        $plan.xss = Math.round(hourlyXss/60 * $plan.duration)
        if (isManual() && $plan.manualXss) $plan.xss = $plan.manualXss.xlss + $plan.manualXss.xhss + $plan.manualXss.xpss
        focusPower.updateFocusPower($plan.targetAthlete)
    }

    // Typescript work around to use (as)
    function updateTerrainFromType() {
        $plan.terrain = Object.keys(eventOptions[$plan.type].terrains)[0] as TerrainTypes
    }
    
    /**
     * Responsible for setting the plan to the default values of the component
    */
    onMount(() => {

        // Setting the defaults for the page
        targetDate = LocalDate.format($plan.targetDate)
        updateEventPlan()

        // Init the page with computed values
        updateInitialInputs()

    })

    async function clearForecastInputs() {
        forecastInputs = null
        await tick() // await to ensure DOM updates
    }

    let initialInputs = null
    async function updateInitialInputs() {
        if (isManual()) await setFocusFromManualXss()
        initialInputs = await initForecastInputs({
            start: $plan.targetDate,
            startDate: $plan.startDate,
            xss: $plan.xss,
            focus: $plan.targetAthlete,
            specificity_rating: $plan.specificity,
            mode: "target_xss",
        })

        updateEventTargets()
    }

    async function updateEventTargets() {
        if (isManual()) await setFocusFromManualXss()
        await clearForecastInputs()
        forecastInputs = getForecastInputs()
            
        if (eventReadinessLocked) {
            await getBestReadinessInputs()
        } else {
            await setTargets(forecastInputs)   
        }

        validateTargets()
    }

    function getForecastInputs() {
        return createForecastInputs<EventForecastTargets>(
            {   
                program: ProgramType.Event,
                xss: $plan.xss,
                focus: $plan.targetAthlete,
                specificity_rating: $plan.specificity,
                start: $plan.targetDate,
                currentFocusPower: $currentFocusPower,
                manualXss: isManual() ? $plan.manualXss : null,
            },
            $userParams.athletes,
            initialInputs,
            $plan.settings
        )
    }

    async function setTargets(forecastInputs) {
        let targets = await estimateTargets(forecastInputs)
        targetTL = targets.targetTL
        targetSignature = targets.targetSignature
        targetXSSBreakdown = targets.targetXSSBreakdown
    }

    // This function updates everything globally
    async function getBestReadinessInputs() {

        await setTargets(forecastInputs)

        if (!validateTargets()) { // Increase readiness value until max
            while (($plan.settings.taper < 5) && (!validateTargets())) {
                $plan.settings.taper = Math.round(($plan.settings.taper + 0.1 )*10)/10
                forecastInputs.options.eventRestDays = $plan.settings.taper
                await setTargets(forecastInputs)
            } 
            return 
        } else { // Decrease readiness value until min 
            while (($plan.settings.taper > 1)) {
                $plan.settings.taper = Math.round(($plan.settings.taper - 0.1 )*10)/10
                forecastInputs.options.eventRestDays = $plan.settings.taper
                await setTargets(forecastInputs)

                if (!validateTargets()) { // The min was the previous value
                    $plan.settings.taper = Math.round(($plan.settings.taper + 0.1 )*10)/10
                    forecastInputs.options.eventRestDays = $plan.settings.taper
                    await setTargets(forecastInputs)
                    return
                }
            }
            return  // return inputs if 1
        }
    }

    /**
     * When changing settings on validation needs to be rerun. It doesn't affect the estimated targets.
     */
    async function refreshForecastInputs() {
        await clearForecastInputs()
        forecastInputs = getForecastInputs()
        validateTargets()
    }

</script>

<div class={className}>
    <div class="flex flex-col gap-5 flex-1 md:flex-row">
        <div class="basis-2/5 grid grid-cols-[2fr,5fr] auto-rows-max items-start gap-y-8 gap-x-3 md:gap-x-6 md:text-right text-left text-base md:grid">

            <slot name="adapt-choice"/>

            {#if !hideStartDate}
            <span class="text-slate-600">Start Date</span>
            <DatePicker class="w-52 text-base" min={LocalDate.format(new Date())} max={LocalDate.format(addDays($plan.targetDate, -1))} {disabled}
                bind:value={startDate}
                on:input={ () => {
                    $plan.startDate = startDateFromInput(startDate)
                    updateInitialInputs()
                }}
            />
            <FutureStartNote startDate={$plan.startDate} />
            {/if}

            <span class="text-slate-600">End Date</span>
            <DatePicker class="w-52 text-base" min={LocalDate.format($plan.startDate ? addDays($plan.startDate, 1) : new Date())} {disabled}
                bind:value={targetDate}
                on:input={ () => {
                    $plan.targetDate = LocalDate.atNoon(targetDate)
                    updateInitialInputs()
                }}
            />

            <span class="text-slate-600">Name</span>
            <input class="input-xfai" type="text" minlength="4" maxlength="20" {disabled} bind:value={$plan.name}/> 

            <span class="text-slate-600">Event Targets</span>
            <RadioGroup.Root class="twp grid grid-cols-[min-content,auto] gap-x-3 gap-y-2 items-center text-left" {disabled}
                value={$plan.targetsMode}
                onValueChange={onTargetsModeValueChange}
            >
                <RadioGroup.Item value="event" id="event-targets-event" />
                <Label class="font-normal text-base mb-0" for="event-targets-event">Choose an Event</Label>
                <RadioGroup.Item value="manual" id="event-targets-manual" />
                <Label class="font-normal text-base mb-0" for="event-targets-manual">Manual Event XSS</Label>
            </RadioGroup.Root>

            {#if $plan.targetsMode === "manual"}
            <span class="text-slate-600">Low XSS</span>
            <input class="input-xfai w-32" type="number" min="0" step="1" {disabled} bind:value={manualXlss} on:input={onManualXssInput}/>

            <span class="text-slate-600">High XSS</span>
            <input class="input-xfai w-32" type="number" min="0" step="0.1" {disabled} bind:value={manualXhss} on:input={onManualXssInput}/>

            <span class="text-slate-600">Peak XSS</span>
            <input class="input-xfai w-32" type="number" min="0" step="0.1" {disabled} bind:value={manualXpss} on:input={onManualXssInput}/>
            {:else}
            <span class="text-slate-600">Event Type</span>
            <select class="input-xfai" {disabled}
                bind:value={$plan.type}
                on:change={() => {
                    updateTerrainFromType()
                    updateEventPlan()
                    updateEventTargets()
                }}
            >
                {#each Object.keys(eventOptions) as key}
                    {#if !eventOptions[key].disabled}
                        <option value={key}>{key}</option>
                    {/if}
                {/each}
            </select>
            
            <span class="text-slate-600">Terrain</span>
            <select class="input-xfai" {disabled}
                bind:value={$plan.terrain}
                on:change={() => {
                    updateEventPlan()
                    updateEventTargets()
                }}
            >
                {#each Object.keys(eventOptions[$plan.type].terrains) as key}
                    <option value={key}>{key}</option>
                {/each}
            </select>

            <span class="text-slate-600">Event Duration</span>
            <div class="flex flex-col gap-1 items-center">
                <SelectSlider min={60} max={maxDurationMinutes} step={5} {disabled}
                    bind:value={$plan.duration}
                    on:input={() =>{
                        updateEventPlan()
                        updateEventTargets()
                    }}
                /> 
                <span class="font-bold">{durationToHoursMinutes($plan.duration)}</span>
            </div>
            {/if}

            <span class="text-slate-600">Event<br/>Readiness <InfoIcon href="http://baronbiosys.com/glossary/event-readiness"/></span>
            <div class="flex flex-col">
                {#if !eventReadinessLocked}
                <div transition:slide>
                    <NumberInput {...eventReadinessBounds} class="max-w-[8rem]" disabled={disabled || eventReadinessLocked}
                        bind:value={$plan.settings.taper}
                        on:change={updateEventTargets} 
                    />
                </div>
                {/if}
                <ToggleButtonSmall class="justify-self-start text-sm text-slate-500 mt-3" label={"Auto-calculate Readiness"}
                    bind:isChecked={eventReadinessLocked}
                    on:change={updateEventTargets}
                />
            </div>

            <span class="text-slate-600">Max Weekly Hours</span>
            <WeeklyHoursSlider {disabled} hoursAchievability={$hoursAchievability.status}
                bind:hours={$plan.settings.maxWeeklyHours}
                onInput={refreshForecastInputs}
            />

            <TrainPrefDayPicker class='col-span-2' bind:cal_settings={$plan.cal_settings} onSubmit={refreshForecastInputs}/>
            
            <XfaiGeneralSettings class="col-span-2" bind:settings={$plan.settings} {disabled} onInput={refreshForecastInputs}/>

        </div>

        <div class="h-px md:w-px md:h-auto bg-black/10"/>

        <div class="basis-3/5 flex flex-col gap-10 text-base">
            
            <div class="grid gap-x-4 gap-y-4 items-start auto-cols-max leading-none">

                <span class="row-start-1 text-sm text-slate-500">Focus</span> 
                <span class="font-semibold">
                    {$plan.targetAthlete}
                    <div class="text-sm leading-none text-slate-600 font-normal">
                        {$focusPowerDuration} minute power
                    </div>
                </span>

                <span class="row-start-2 text-sm text-slate-500">Specificity</span>
                <span class="row-start-2 font-semibold">
                    {$plan.specificity}
                    <span class="text-slate-600 font-normal text-sm leading-none">
                        {specificityThreshold[$plan.specificity]}
                    </span>
                </span>

                <span class="row-start-3 text-sm text-left text-slate-500 inline-flex items-baseline gap-2">
                    XSS
                    <ProgramAchievabilityStatusIcon status={$overallAchievability} showCheck/>
                </span>
                {#if targetXSSBreakdown}
                    <XfaiXss class="border-dashed border rounded px-3 py-2 border-slate-600" xss={targetXSSBreakdown} />  
                {/if}

                <span class="row-start-4 text-sm text-slate-600">
                    Event<br/>Readiness
                    <InfoIcon href={"http://baronbiosys.com/glossary/event-readiness"} />
                </span>
                <div class="flex gap-2 items-center">
                    <EventReadiness taper={$plan.settings.taper} />
                    <i class="fa-thin" class:fa-lock={eventReadinessLocked} class:fa-lock-open={!eventReadinessLocked} />
                </div>
                
            </div>

            <!-- Status -->
            <div id={achievabilityId} class="flex flex-col gap-3 text-left">
                <span class="font-bold">
                    Achievability
                    <InfoIcon href={"http://baronbiosys.com/glossary/achievability-event"} />
                </span>
                <ProgramAchievabilityMessage statusMessage={$statusMessage} status={$overallAchievability}/>
            </div>

            <ProgramTrainingLoad 
                targetAchievability={$targetAchievability} {currentTL} {targetTL}
                description = "Required to complete the event"
            />

            <ProgramFitnessSignature 
                targetAchievability={$targetAchievability} {currentSignature} {targetSignature}
                description = "Achieved on completion of required training loads"
            />   
        </div>
    </div>
</div>